import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { api, API } from "../lib/api";
import { playNotifSound } from "../lib/push";
import { useAuth } from "./AuthContext";

/*
 * NotificationProvider — YON SÈL koneksyon WebSocket global.
 *
 * - Konekte ak /api/ws?token=TOKEN
 * - Resevwa notifikasyon an tan reyèl
 * - Jwe son sèlman lè aplikasyon an vizib
 * - Service Worker okipe push lè aplikasyon an kache/fèmen
 * - Reconnect otomatik
 * - Fèmen socket la lè token chanje oswa logout
 */

const RETRY_DELAYS = [1000, 2000, 5000, 10000, 30000];

const NotificationContext = createContext({
    unread: 0,
    items: [],
    latest: null,
    markAllRead: () => {},
    dismissLatest: () => {},
    reload: () => {},
});

function buildWsUrl(token) {
    try {
        const url = new URL(API);

        url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
        url.pathname = `${url.pathname.replace(/\/$/, "")}/ws`;
        url.search = `?token=${encodeURIComponent(token)}`;
        url.hash = "";

        return url.toString();
    } catch {
        return null;
    }
}

export function NotificationProvider({ children }) {
    const { token } = useAuth();

    const [unread, setUnread] = useState(0);
    const [items, setItems] = useState([]);
    const [latest, setLatest] = useState(null);

    const wsRef = useRef(null);
    const retryRef = useRef(0);
    const timerRef = useRef(null);
    const closedByUs = useRef(false);

    const loadHistory = useCallback(async () => {
        try {
            const { data } = await api.get("/me/notifications");

            setItems(Array.isArray(data?.items) ? data.items : []);
            setUnread(Number(data?.unread) || 0);
        } catch {
            // Pa kraze aplikasyon an si istwa a pa chaje.
        }
    }, []);

    const markAllRead = useCallback(async () => {
        try {
            await api.post("/me/notifications/read-all");

            setUnread(0);
            setItems((previousItems) =>
                previousItems.map((notification) => ({
                    ...notification,
                    read: true,
                })),
            );
        } catch {
            // Pa kraze aplikasyon an si demann lan echwe.
        }
    }, []);

    /*
     * Service Worker la voye PLAY_NOTIF_SOUND lè yon Web Push rive.
     * Nou jwe son an sèlman si aplikasyon an vizib.
     */
    useEffect(() => {
        if (!("serviceWorker" in navigator)) {
            return undefined;
        }

        const handleServiceWorkerMessage = (event) => {
            if (
                event.data?.type === "PLAY_NOTIF_SOUND" &&
                document.visibilityState === "visible"
            ) {
                playNotifSound();
            }
        };

        navigator.serviceWorker.addEventListener(
            "message",
            handleServiceWorkerMessage,
        );

        return () => {
            navigator.serviceWorker.removeEventListener(
                "message",
                handleServiceWorkerMessage,
            );
        };
    }, []);

    useEffect(() => {
        if (!token) {
            closedByUs.current = true;

            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }

            try {
                wsRef.current?.close();
            } catch {
                // Ignore.
            }

            wsRef.current = null;
            setUnread(0);
            setItems([]);
            setLatest(null);

            return undefined;
        }

        closedByUs.current = false;
        retryRef.current = 0;

        const connect = () => {
            if (closedByUs.current) {
                return;
            }

            /*
             * Pa kreye yon lòt socket si youn deja ap konekte
             * oswa li deja ouvri.
             */
            if (
                wsRef.current &&
                (
                    wsRef.current.readyState === WebSocket.CONNECTING ||
                    wsRef.current.readyState === WebSocket.OPEN
                )
            ) {
                return;
            }

            const url = buildWsUrl(token);

            if (!url) {
                console.error("URL WebSocket la pa valab.");
                return;
            }

            try {
                const ws = new WebSocket(url);
                wsRef.current = ws;

                let keepAliveInterval = null;

                ws.onopen = () => {
                    if (ws !== wsRef.current) {
                        ws.close();
                        return;
                    }

                    retryRef.current = 0;
                    loadHistory();

                    keepAliveInterval = window.setInterval(() => {
                        if (ws.readyState === WebSocket.OPEN) {
                            try {
                                ws.send("ping");
                            } catch {
                                // Ignore.
                            }
                        }
                    }, 20000);
                };

                ws.onmessage = (event) => {
                    let message;

                    try {
                        message = JSON.parse(event.data);
                    } catch {
                        return;
                    }

                    if (message.event === "hello") {
                        setUnread(Number(message.payload?.unread) || 0);
                        return;
                    }

                    if (message.event === "ping") {
                        if (ws.readyState === WebSocket.OPEN) {
                            try {
                                ws.send("pong");
                            } catch {
                                // Ignore.
                            }
                        }

                        return;
                    }

                    if (message.event !== "notification") {
                        return;
                    }

                    const payload = message.payload || {};

                    setUnread((currentUnread) => currentUnread + 1);

                    setItems((previousItems) => {
                        /*
                         * Evite mete menm notifikasyon an plizyè fwa
                         * si backend ak WebSocket la retounen menm ID a.
                         */
                        const notificationId =
                            payload.id ||
                            payload._id ||
                            payload.notification_id;

                        if (
                            notificationId &&
                            previousItems.some(
                                (item) =>
                                    item.id === notificationId ||
                                    item._id === notificationId ||
                                    item.notification_id === notificationId,
                            )
                        ) {
                            return previousItems;
                        }

                        return [
                            {
                                ...payload,
                                read: false,
                            },
                            ...previousItems,
                        ].slice(0, 100);
                    });

                    setLatest(payload);

                    /*
                     * Lè paj la vizib, jwe son aplikasyon an.
                     * Lè paj la kache oswa fèmen, Service Worker la
                     * okipe notifikasyon sistèm nan.
                     */
                    if (document.visibilityState === "visible") {
                        playNotifSound();
                    }
                };

                ws.onerror = () => {
                    /*
                     * Pa fè reconnect isit la.
                     * onclose ap okipe reconnect la.
                     */
                };

                ws.onclose = () => {
                    if (keepAliveInterval) {
                        clearInterval(keepAliveInterval);
                    }

                    if (wsRef.current === ws) {
                        wsRef.current = null;
                    }

                    if (closedByUs.current) {
                        return;
                    }

                    const retryIndex = Math.min(
                        retryRef.current,
                        RETRY_DELAYS.length - 1,
                    );

                    const delay = RETRY_DELAYS[retryIndex];
                    retryRef.current += 1;

                    if (timerRef.current) {
                        clearTimeout(timerRef.current);
                    }

                    timerRef.current = window.setTimeout(() => {
                        timerRef.current = null;
                        connect();
                    }, delay);
                };
            } catch {
                if (closedByUs.current) {
                    return;
                }

                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                }

                timerRef.current = window.setTimeout(() => {
                    timerRef.current = null;
                    connect();
                }, 3000);
            }
        };

        loadHistory();
        connect();

        return () => {
            closedByUs.current = true;

            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }

            const currentSocket = wsRef.current;
            wsRef.current = null;

            if (currentSocket) {
                currentSocket.onopen = null;
                currentSocket.onmessage = null;
                currentSocket.onerror = null;
                currentSocket.onclose = null;

                try {
                    currentSocket.close();
                } catch {
                    // Ignore.
                }
            }
        };
    }, [token, loadHistory]);

    const dismissLatest = useCallback(() => {
        setLatest(null);
    }, []);

    return (
        <NotificationContext.Provider
            value={{
                unread,
                items,
                latest,
                markAllRead,
                dismissLatest,
                reload: loadHistory,
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
}

export const useNotifications = () => useContext(NotificationContext);